package com.example.mystybag.login.logic

import com.example.mystybag.common.resources.SR
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.Response
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.model.CityItem
import com.example.mystybag.core.model.CountryItem
import com.example.mystybag.login.model.CompanyContactRequiredField
import com.example.mystybag.login.model.MerchantRegistrationAddCompanyContactUIState
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GenerateMerchantPhoneVerificationCodeRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.VerifyMerchantPhoneVerificationCodeRequestModel
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch

class MerchantRegistrationAddCompanyContactViewModel(
    context: AppContext,
    private val config: NavigatorMerchantRegistrationStep1B
) : ChildViewModel<MerchantRegistrationAddCompanyContactUIState>(context, MerchantRegistrationAddCompanyContactUIState()) {

    private var smsResendCooldownJob: Job? = null

    private val sharedConfig: MerchantRegistrationSharedConfig
        get() = config.sharedConfig

    private fun cancelSmsResendCooldown() {
        smsResendCooldownJob?.cancel()
        smsResendCooldownJob = null
        updateState { s -> if (s.smsResendCooldownSeconds != 0) s.copy(smsResendCooldownSeconds = 0) else s }
    }

    private fun startSmsResendCooldown() {
        smsResendCooldownJob?.cancel()
        smsResendCooldownJob = launchOnMain {
            var remaining = 60
            updateState { it.copy(smsResendCooldownSeconds = remaining) }
            while (remaining > 0 && isActive) {
                delay(1000)
                if (!isActive) return@launchOnMain
                remaining--
                updateState { it.copy(smsResendCooldownSeconds = remaining) }
            }
        }
    }

    init {
        syncStateFromSharedConfig()
    }

    override fun onCreate() {
        navControls.backButtonVisible = true
        navControls.title = sharedString(SR.strings.registration)
        // Re-sync from sharedConfig when screen is shown (data may have been set after init, e.g. from CompanyBook fetch)
        syncStateFromSharedConfig()
        loadCities()
        loadCountries()
    }

    /** Copies sharedConfig into UI state so that CompanyBook-loaded data is visible on the form. */
    private fun syncStateFromSharedConfig() {
        val phoneAlreadyVerified = sharedConfig.merchantPhoneVerificationCode.isNotBlank()
        if (phoneAlreadyVerified) cancelSmsResendCooldown()
        updateState {
            it.copy(
                legalEntityName = sharedConfig.legalEntityName,
                registeredCompanyStreet = sharedConfig.registeredCompanyStreet,
                registeredCompanyCountryId = sharedConfig.registeredCompanyCountryId,
                registeredCompanyCountryName = sharedConfig.registeredCompanyCountryName,
                registeredCompanyCityId = sharedConfig.registeredCompanyCityId,
                registeredCompanyCityName = sharedConfig.registeredCompanyCityName,
                companyPhoneNumber = sharedConfig.companyPhoneNumber,
                companyDialCode = sharedConfig.companyDialCode,
                privacyPolicyAccepted = sharedConfig.privacyPolicyAccepted,
                awaitingSmsVerification = if (phoneAlreadyVerified) false else it.awaitingSmsVerification,
                smsVerificationCode = if (phoneAlreadyVerified) "" else it.smsVerificationCode,
                smsResendCooldownSeconds = if (phoneAlreadyVerified) 0 else it.smsResendCooldownSeconds
            )
        }
    }

    private fun persistFormFromState(state: MerchantRegistrationAddCompanyContactUIState) {
        sharedConfig.legalEntityName = state.legalEntityName
        sharedConfig.registeredCompanyStreet = state.registeredCompanyStreet
        sharedConfig.registeredCompanyCountryId = state.registeredCompanyCountryId
        sharedConfig.registeredCompanyCountryName = state.registeredCompanyCountryName
        sharedConfig.registeredCompanyCityId = state.registeredCompanyCityId
        sharedConfig.registeredCompanyCityName = state.registeredCompanyCityName
        sharedConfig.companyPhoneNumber = state.companyPhoneNumber
        sharedConfig.companyDialCode = state.companyDialCode
        sharedConfig.privacyPolicyAccepted = true
    }

    private fun phoneMatchesStoredVerification(state: MerchantRegistrationAddCompanyContactUIState): Boolean =
        sharedConfig.merchantPhoneVerificationCode.isNotBlank() &&
            sharedConfig.companyPhoneNumber == state.companyPhoneNumber &&
            sharedConfig.companyDialCode == state.companyDialCode

    private fun loadCities() {
        scope.launch {
            commandBlock(showProgress = false) {
                CommonCommands.sendGetCities()
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val cities = response.citiesList.map { city ->
                                    CityItem(
                                        id = city.id,
                                        name = city.cityName,
                                        countryId = city.countryId,
                                        latitude = city.latitude,
                                        longitude = city.longitude
                                    )
                                }
                                
                                updateState {
                                    it.copy(cities = cities)
                                }
                                // Re-apply sharedConfig in case CompanyBook data arrived after onCreate
                                syncStateFromSharedConfig()
                                
                                // Try to match city by name if we have a city name from companybook
                                // Always try to match, even if cityId is already set, to ensure we have the correct ID
                                if (sharedConfig.registeredCompanyCityName.isNotBlank()) {
                                    // Convert both to uppercase for comparison
                                    val cityNameFromConfig = sharedConfig.registeredCompanyCityName.uppercase()
                                    val matchedCity = cities.find { city ->
                                        city.name.uppercase() == cityNameFromConfig ||
                                        city.name.uppercase().contains(cityNameFromConfig) ||
                                        cityNameFromConfig.contains(city.name.uppercase())
                                    }
                                    matchedCity?.let { city ->
                                        // Always update when we find a match to ensure UIState is correct
                                        // Use the name from our nomenclature
                                        selectRegisteredCompanyCity(city.id, city.name)
                                    }
                                }
                                
                                // Also check if we have cityId in sharedConfig but UIState doesn't have it
                                if (sharedConfig.registeredCompanyCityId != null && uiState.value.registeredCompanyCityId == null) {
                                    val cityById = cities.find { it.id == sharedConfig.registeredCompanyCityId }
                                    cityById?.let { city ->
                                        updateState {
                                            it.copy(
                                                registeredCompanyCityId = city.id,
                                                registeredCompanyCityName = city.name
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    )
            }
        }
    }

    private fun loadCountries() {
        scope.launch {
            commandBlock(showProgress = false) {
                CommonCommands.sendGetCountries()
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val countries = response.countriesList?.map { country ->
                                    CountryItem(
                                        id = country.id,
                                        name = country.name,
                                        dialCode = country.dialCode,
                                        isoAlpha2 = country.isoAlpha2
                                    )
                                } ?: emptyList()
                                
                                updateState {
                                    it.copy(countries = countries)
                                }
                                // Re-apply sharedConfig in case CompanyBook data arrived after onCreate
                                syncStateFromSharedConfig()
                                
                                // Try to match country by name if we have a country name from companybook
                                // Always try to match, even if countryId is already set, to ensure we have the correct ID
                                if (sharedConfig.registeredCompanyCountryName.isNotBlank()) {
                                    // Convert both to uppercase for comparison
                                    val countryNameFromConfig = sharedConfig.registeredCompanyCountryName.uppercase()
                                    val matchedCountry = countries.find { country ->
                                        country.name.uppercase() == countryNameFromConfig ||
                                        country.name.uppercase().contains(countryNameFromConfig) ||
                                        countryNameFromConfig.contains(country.name.uppercase())
                                    }
                                    matchedCountry?.let { country ->
                                        // Always update when we find a match to ensure UIState is correct
                                        // Use the name from our nomenclature
                                        selectRegisteredCompanyCountry(country.id, country.name)
                                    }
                                }
                                
                                // Also check if we have countryId in sharedConfig but UIState doesn't have it
                                if (sharedConfig.registeredCompanyCountryId != null && uiState.value.registeredCompanyCountryId == null) {
                                    val countryById = countries.find { it.id == sharedConfig.registeredCompanyCountryId }
                                    countryById?.let { country ->
                                        updateState {
                                            it.copy(
                                                registeredCompanyCountryId = country.id,
                                                registeredCompanyCountryName = country.name
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    )
            }
        }
    }

    fun updateLegalEntityName(name: String) {
        updateState {
            it.copy(
                legalEntityName = name,
                requiredFieldErrors = it.requiredFieldErrors - CompanyContactRequiredField.LEGAL_ENTITY_NAME
            )
        }
        sharedConfig.legalEntityName = name
    }

    fun updateRegisteredCompanyStreet(street: String) {
        updateState {
            it.copy(
                registeredCompanyStreet = street,
                requiredFieldErrors = it.requiredFieldErrors - CompanyContactRequiredField.ADDRESS
            )
        }
        sharedConfig.registeredCompanyStreet = street
    }

    fun showCityPicker() {
        updateState { it.copy(showCityPicker = true) }
    }

    fun hideCityPicker() {
        updateState { it.copy(showCityPicker = false) }
    }

    fun selectRegisteredCompanyCity(cityId: Long, cityName: String) {
        updateState {
            it.copy(
                registeredCompanyCityId = cityId,
                registeredCompanyCityName = cityName,
                showCityPicker = false,
                requiredFieldErrors = it.requiredFieldErrors - CompanyContactRequiredField.CITY
            )
        }
        sharedConfig.registeredCompanyCityId = cityId
        sharedConfig.registeredCompanyCityName = cityName
    }

    fun showCountryPicker() {
        updateState { it.copy(showCountryPicker = true) }
    }

    fun hideCountryPicker() {
        updateState { it.copy(showCountryPicker = false) }
    }

    fun selectRegisteredCompanyCountry(countryId: Int, countryName: String) {
        updateState {
            it.copy(
                registeredCompanyCountryId = countryId,
                registeredCompanyCountryName = countryName,
                showCountryPicker = false,
                requiredFieldErrors = it.requiredFieldErrors - CompanyContactRequiredField.COUNTRY
            )
        }
        sharedConfig.registeredCompanyCountryId = countryId
        sharedConfig.registeredCompanyCountryName = countryName
    }

    fun updateCompanyPhoneNumber(phoneNumber: String) {
        // Filter to allow only digits
        val filteredPhone = phoneNumber.filter { it.isDigit() }
        cancelSmsResendCooldown()
        updateState {
            it.copy(
                companyPhoneNumber = filteredPhone,
                awaitingSmsVerification = false,
                smsVerificationCode = "",
                smsResendCooldownSeconds = 0,
                requiredFieldErrors = it.requiredFieldErrors - CompanyContactRequiredField.PHONE_NUMBER
            )
        }
        sharedConfig.companyPhoneNumber = filteredPhone
        sharedConfig.merchantPhoneVerificationCode = ""
    }

    fun showCompanyPhoneCountryPicker() {
        updateState { it.copy(showCompanyPhoneCountryPicker = true) }
    }

    fun hideCompanyPhoneCountryPicker() {
        updateState { it.copy(showCompanyPhoneCountryPicker = false) }
    }

    fun selectCompanyPhoneCountry(countryId: Int, dialCode: String) {
        cancelSmsResendCooldown()
        updateState {
            it.copy(
                companyDialCode = dialCode,
                showCompanyPhoneCountryPicker = false,
                awaitingSmsVerification = false,
                smsVerificationCode = "",
                smsResendCooldownSeconds = 0,
                requiredFieldErrors = it.requiredFieldErrors - CompanyContactRequiredField.DIAL_CODE
            )
        }
        sharedConfig.companyDialCode = dialCode
        sharedConfig.merchantPhoneVerificationCode = ""
    }

    fun togglePrivacyPolicy() {
        val newValue = !uiState.value.privacyPolicyAccepted
        updateState { it.copy(privacyPolicyAccepted = newValue) }
        sharedConfig.privacyPolicyAccepted = newValue
    }
    
    override fun onBackButtonClicked() {
        // Update shared config with current state before going back
        val state = uiState.value
        sharedConfig.legalEntityName = state.legalEntityName
        sharedConfig.registeredCompanyStreet = state.registeredCompanyStreet
        sharedConfig.registeredCompanyCountryId = state.registeredCompanyCountryId
        sharedConfig.registeredCompanyCountryName = state.registeredCompanyCountryName
        sharedConfig.registeredCompanyCityId = state.registeredCompanyCityId
        sharedConfig.registeredCompanyCityName = state.registeredCompanyCityName
        sharedConfig.companyPhoneNumber = state.companyPhoneNumber
        sharedConfig.companyDialCode = state.companyDialCode
        sharedConfig.privacyPolicyAccepted = state.privacyPolicyAccepted
        
        // Update current config in stack
        val updatedConfig = config.copy(sharedConfig = sharedConfig)
        replaceLast(updatedConfig)
        
        super.onBackButtonClicked()
    }

    fun updateSmsVerificationCode(code: String) {
        val digits = code.filter { it.isDigit() }.take(6)
        updateState {
            it.copy(
                smsVerificationCode = digits,
                requiredFieldErrors = it.requiredFieldErrors - CompanyContactRequiredField.SMS_CODE
            )
        }
    }

    fun resendPhoneVerificationSms() {
        val state = uiState.value
        if (!state.awaitingSmsVerification || state.isLoading || state.smsResendCooldownSeconds > 0) return
        requestPhoneVerificationSms()
    }

    fun onContinue() {
        val state = uiState.value
        if (state.isLoading) return

        if (state.awaitingSmsVerification) {
            if (state.smsVerificationCode.length != 6) {
                updateState {
                    it.copy(
                        requiredFieldErrors = it.requiredFieldErrors + CompanyContactRequiredField.SMS_CODE,
                        validationRequestId = it.validationRequestId + 1
                    )
                }
                showSnackbar(sharedString(SR.strings.please_enter_all_required_fields))
                return
            }
            verifySmsAndContinue(state.smsVerificationCode)
            return
        }

        val missing = mutableSetOf<CompanyContactRequiredField>()
        if (state.legalEntityName.isBlank()) missing += CompanyContactRequiredField.LEGAL_ENTITY_NAME
        if (state.registeredCompanyCountryId == null) missing += CompanyContactRequiredField.COUNTRY
        if (state.registeredCompanyCityId == null) missing += CompanyContactRequiredField.CITY
        if (state.registeredCompanyStreet.isBlank()) missing += CompanyContactRequiredField.ADDRESS
        if (state.companyDialCode.isBlank()) missing += CompanyContactRequiredField.DIAL_CODE
        if (state.companyPhoneNumber.isBlank()) missing += CompanyContactRequiredField.PHONE_NUMBER

        if (missing.isNotEmpty()) {
            updateState {
                it.copy(
                    requiredFieldErrors = missing,
                    validationRequestId = it.validationRequestId + 1
                )
            }
            showSnackbar(sharedString(SR.strings.please_enter_all_required_fields))
            return
        }

        // Phone already verified (e.g. user returned from next step): Next goes to step 2 without new SMS
        if (phoneMatchesStoredVerification(state)) {
            persistFormFromState(state)
            replaceLast(config.copy(sharedConfig = sharedConfig))
            navigate(NavigatorMerchantRegistrationStep2(sharedConfig = sharedConfig))
            return
        }

        persistFormFromState(state)
        sharedConfig.merchantPhoneVerificationCode = ""
        replaceLast(config.copy(sharedConfig = sharedConfig))
        requestPhoneVerificationSms()
    }

    private fun requestPhoneVerificationSms() {
        val state = uiState.value
        scope.launch {
            updateState { it.copy(isLoading = true) }
            commandBlock(showProgress = true) {
                CommonCommands.generateMerchantPhoneVerificationCode(
                    GenerateMerchantPhoneVerificationCodeRequestModel(
                        dialCode = state.companyDialCode,
                        phoneNumber = state.companyPhoneNumber
                    )
                )
                    .onResponse(
                        success = {
                            launchOnMain {
                                updateState {
                                    it.copy(
                                        isLoading = false,
                                        awaitingSmsVerification = true,
                                        smsVerificationCode = ""
                                    )
                                }
                                startSmsResendCooldown()
                            }
                        },
                        baseError = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                showSnackbar(
                                    Response.getErrorMessage(
                                        response.status,
                                        response.apiEndPoint,
                                        response.statusMessage
                                    )
                                )
                            }
                        }
                    )
            }
        }
    }

    private fun verifySmsAndContinue(code: String) {
        val state = uiState.value
        scope.launch {
            updateState { it.copy(isLoading = true) }
            commandBlock(showProgress = true) {
                CommonCommands.verifyMerchantPhoneVerificationCode(
                    VerifyMerchantPhoneVerificationCodeRequestModel(
                        dialCode = state.companyDialCode,
                        phoneNumber = state.companyPhoneNumber,
                        verificationCode = code
                    )
                )
                    .onResponse(
                        success = {
                            launchOnMain {
                                sharedConfig.merchantPhoneVerificationCode = code
                                persistFormFromState(state)

                                replaceLast(config.copy(sharedConfig = sharedConfig))
                                updateState { it.copy(isLoading = false, awaitingSmsVerification = false, smsVerificationCode = "") }
                                navigate(NavigatorMerchantRegistrationStep2(sharedConfig = sharedConfig))
                            }
                        },
                        baseError = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false, smsVerificationCode = "") }
                                showSnackbar(
                                    Response.getErrorMessage(
                                        response.status,
                                        response.apiEndPoint,
                                        response.statusMessage
                                    )
                                )
                            }
                        }
                    )
            }
        }
    }
}
